import React, { useEffect, useRef } from "react";
import { useLang } from "../contexts/LanguageContext";
import { ArrowRight, ArrowDown } from "lucide-react";
import CountUp from "./CountUp";

const HERO_IMG =
  "https://images.unsplash.com/photo-1763431542929-a151b710f0a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxzdW5mbG93ZXIlMjBmaWVsZCUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzc3MTMyMTI3fDA&ixlib=rb-4.1.0&q=85";

const Hero = () => {
  const { t } = useLang();
  const bgRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (!bgRef.current) return;
      const y = window.scrollY;
      bgRef.current.style.transform = `translate3d(0, ${y * 0.25}px, 0) scale(1.08)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] w-full overflow-hidden flex flex-col"
    >
      {/* Parallax background */}
      <div
        ref={bgRef}
        className="absolute inset-0 will-change-transform"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: "scale(1.08)",
        }}
        aria-hidden="true"
      />
      {/* Gradient overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-black/10"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/10"
        aria-hidden="true"
      />
      {/* Grain texture */}
      <div
        className="absolute inset-0 opacity-[0.12] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 300 300' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32 pb-24">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6 reveal">
              <span className="divider-line" />
              <span className="overline text-[#F1C779]">{t.hero.overline}</span>
            </div>

            <h1
              className="text-white leading-[1.05] tracking-tight reveal"
              style={{ animationDelay: "100ms" }}
            >
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-[5.5rem] font-bold">
                {t.hero.title1}
              </span>
              <span className="block text-4xl sm:text-5xl md:text-6xl lg:text-[5.5rem] font-bold shine-text">
                {t.hero.title2}
              </span>
            </h1>

            <p className="mt-8 max-w-2xl text-base sm:text-lg text-white/85 leading-relaxed font-light reveal">
              {t.hero.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap gap-4 reveal">
              <a
                href="#products"
                data-testid="hero-cta-products"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-medium hover:bg-[#FDFBF7] transition-colors"
              >
                {t.hero.cta1}
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#contact"
                data-testid="hero-cta-quote"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/40 text-white text-xs tracking-[0.22em] uppercase hover:border-white hover:bg-white/10 transition-colors"
              >
                {t.hero.cta2}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Stats strip */}
      <div className="relative z-10 border-t border-white/15 backdrop-blur-sm bg-black/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-3 gap-4 sm:gap-12">
            <Stat value="22+" label={t.hero.stat1} />
            <Stat value="90" label={t.hero.stat2} />
            <Stat value="20+" label={t.hero.stat3} />
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <a
        href="#about"
        className="hidden md:flex absolute bottom-28 right-8 z-10 flex-col items-center gap-2 text-white/70 hover:text-white transition-colors"
      >
        <span className="overline">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </a>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div className="reveal">
    <div className="text-3xl sm:text-4xl text-[#DE9E36] font-bold">
      <CountUp value={value} />
    </div>
    <div className="overline text-white/70 mt-1">{label}</div>
  </div>
);

export default Hero;
